import React from 'react';
import AnimatedNumber from 'react-animated-number';
import { Cx } from 'tss-react';
import { makeStyles } from 'tss-react/mui';
import { SkodaCheckboxCheckerIcon } from '../../components/icons';
import { notNullOrEmpty } from '../../utils/common';
import { ImageLoader } from '../imageLoader';
import { LinearProgressDelayed } from '../linearProgressDelayed';

const useStyles = makeStyles({ name: 'EmobilityResultPanel' })(theme => ({
	root: {
		padding: '32px 20px',
		background: theme.palette.background.paper,
		display: 'flex',
		flexDirection: 'column',
		position: 'relative',
		zIndex: 0
	},
	resultLabel: {
		fontSize: '18px',
		lineHeight: '18px',
		marginBottom: '8px'
	},
	result: {
		fontSize: '33px',
		lineHeight: '40px',
		marginBottom: '24px',
		fontWeight: 'bold',
		display: 'block',
		'&.nobonus': {
			marginBottom: '50px'
		}
	},
	panelContent: {
		display: 'flex',
		flexDirection: 'column',
		margin: '-7.5px 0'
	},
	panelBonuses: {
		display: 'flex',
		flexDirection: 'column',
		padding: '7.5px 0',
		margin: -7
	},
	bonus: {
		padding: 7,
		display: 'flex',
		fontSize: '16px',
		lineHeight: '24px'
	},
	checker: {
		marginRight: '10px',
		color: theme.palette.bulletPoints
	},
	image: {
		alignSelf: 'center',
		marginTop: 'calc(-17.507% - 5px)',
		marginBottom: 'calc(-11.298% - 5px)',
		padding: '7.5px 0',
		width: 'calc(100% + 40px)',
		height: 'auto',
		zIndex: -1
	},
	panelButtonAndNote: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		paddingTop: 30,
		margin: -8
	},
	containerNote: {
		flexGrow: 1,
		padding: 8
	},
	containerButton: {
		flexShrink: 0,
		padding: 8
	},
	dividerFooter: {
		height: 1,
		width: '100%',
		marginTop: 32,
		background: '#E0E3E6',
		marginBottom: 24
	},
	loader: {
		position: 'absolute',
		top: '0px',
		left: '0px',
		right: '0px'
	}
}));

export type EmobilityResultPanelProps = {
	result: number;
	bonuses: string[];
	image: string;
	dummy: string;
	formatResult?: (value: number) => string;
	note?: React.ReactNode;
	button?: React.ReactNode;
	footer?: React.ReactNode;
	calculating: boolean;
	classes?: Partial<ReturnType<typeof useStyles>['classes']>;
	resultRef?: React.LegacyRef<HTMLSpanElement>;
	resultLabel?: React.ReactNode;
};

export const EmobilityResultPanel: React.FC<EmobilityResultPanelProps> = (props) => {
	const { result, formatResult, bonuses, image, dummy, note, button, footer, calculating, resultRef, resultLabel } = props;
	let { classes, cx } = useStyles(undefined, { props });

	if (!notNullOrEmpty(bonuses))
		classes = addNoBonusClass(classes, cx);

	return (
		<div data-test='emobility-result-panel' className={classes.root}>
			{(resultLabel) &&
				<span data-test='result-headline' className={classes.resultLabel}>
					{resultLabel}
				</span>}

			<span ref={resultRef} className={classes.result} data-test='value-result'>
				<AnimatedNumber
					value={result}
					duration={300}
					formatValue={formatResult ? formatResult : undefined} />
			</span>

			<div data-test='panel-content' className={classes.panelContent}>
				{notNullOrEmpty(bonuses) &&
					<div data-test='bonuses' className={classes.panelBonuses}>
						{bonuses.map((text, index) =>
							<div data-test='bonus-item' key={index} className={classes.bonus}>
								<SkodaCheckboxCheckerIcon className={classes.checker} />
								<span>{text}</span>
							</div>)}
					</div>}

				<ImageLoader
					data-test='result-image'
					width={1920}
					height={1080}
					className={classes.image}
					src={image}
					dummy={dummy}
					alt='model render' />
			</div>

			{(button || note) &&
				<div className={classes.panelButtonAndNote}>
					{note && <div className={classes.containerNote}>{note}</div>}
					{button && <div className={classes.containerButton}>{button}</div>}
				</div>}

			{footer && <div className={classes.dividerFooter} />}

			{footer}

			<LinearProgressDelayed className={classes.loader} loading={calculating} />

		</div >
	);
};

const addNoBonusClass = (styles: ReturnType<typeof useStyles>['classes'], cx: Cx) =>
	Object.keys(styles).reduce((prev, curr) => ({ ...prev, [curr]: cx(styles[curr], 'nobonus') }), {} as ReturnType<typeof useStyles>['classes']);
